// Coordinator / case-manager candidate selection for the member list filters.
//
// TRUS-434: the coordinator dropdown must show this org's workforce staff of
// ANY role. An org's case-manager/coordinator roles are CUSTOM role_terminology
// types (e.g. Maui LTRG's "dcm01" / "partner_dcm", which map to the "staff"
// system role) — they are NOT the fixed system-role keys. Filtering candidates
// against a hardcoded allowlist of role keys therefore silently dropped every
// custom-role staffer (all 7 Disaster Case Managers for Maui), leaving only
// org_admin / program_manager in the dropdown.
//
// The correct rule is a denylist: keep every returned staff user, excluding
// only portal members and platform-level admins. The backend already excludes
// these when called with staff_only=true&exclude_internal=true; this helper is
// the defense-in-depth guard and the unit-tested contract.

export interface CoordinatorCandidateUser {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  user_org_role?: string;
  user_type?: string;
}

export interface CoordinatorOption {
  id: number;
  name: string;
  role?: string;
}

// Portal members and platform admins are never org-workforce coordinators.
// Any other role_type — including arbitrary per-org custom roles — is valid.
const EXCLUDED_COORDINATOR_ROLES = new Set(['member', 'super_admin', 'ops_admin']);

// Trust-WeaveIn internal / automation accounts, excluded from any CBO-scoped
// staff picker (mirrors the backend exclude_internal filter).
const INTERNAL_DOMAINS = ['@trust.care', '@weavein.ai', '@payssurance.com', '@anthropic.com'];

const isInternalEmail = (email?: string): boolean => {
  const e = (email || '').toLowerCase();
  return !!e && INTERNAL_DOMAINS.some((d) => e.endsWith(d));
};

export function selectCoordinatorCandidates(
  users: CoordinatorCandidateUser[] | null | undefined
): CoordinatorOption[] {
  return (users || [])
    .filter((u) => {
      // Only actual staff accounts — never portal members or pending invites.
      if (u.user_type && u.user_type !== 'user') return false;
      const role = (u.user_org_role || u.role || '').toLowerCase();
      if (EXCLUDED_COORDINATOR_ROLES.has(role)) return false;
      if (isInternalEmail(u.email)) return false;
      return true;
    })
    .map((u) => ({
      id: u.id,
      name:
        u.name ||
        `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
        u.email ||
        '',
      role: u.user_org_role || u.role,
    }));
}
